import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Spinner, Card, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { isSuperAdmin } from "../../common/helpers"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import IndicatorCard from "../../components/indicators/IndicatorCard"
import SellerRanking from "../../components/indicators/SellerRanking"
import SuperAdminBackLink from "../../components/platform/SuperAdminBackLink"
import moment from "moment"
import { SellerSummaryItem } from "../../types/Indicators.types"
import { Line } from "react-chartjs-2"
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

const CHART_COLORS = [
  "#0d6efd", "#198754", "#dc3545", "#ffc107", "#6f42c1",
  "#fd7e14", "#20c997", "#0dcaf0", "#d63384", "#6c757d"
]

interface OrganizationOption {
  id: string
  name: string
}

export default function SellerIndicatorsPage() {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const canSelectOrganization = isSuperAdmin(user)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(canSelectOrganization ? "" : userOrgId)
  const [sellers, setSellers] = useState<SellerSummaryItem[]>([])
  const [salesByMonth, setSalesByMonth] = useState<any[]>([])
  const [agentVisits, setAgentVisits] = useState<any[]>([])
  const effectiveOrgId = canSelectOrganization ? selectedOrganizationId : userOrgId

  const clearData = useCallback(() => {
    setSellers([])
    setSalesByMonth([])
    setAgentVisits([])
  }, [])

  const loadOrganizations = useCallback(async () => {
    if (!canSelectOrganization) return

    try {
      setIsLoadingOrganizations(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status === "ok") {
        const records = res.data.response ?? []
        setOrganizations(records)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingOrganizations(false)
    }
  }, [canSelectOrganization])

  const bindData = useCallback(async (startDate?: string, endDate?: string) => {
    if (!effectiveOrgId) return

    try {
      setIsLoading(true)
      const start = startDate || moment().startOf("month").format("YYYY-MM-DD")
      const end = endDate || moment().format("YYYY-MM-DD")

      const [sellerRes, salesRes, agentRes] = await Promise.all([
        API.getAction("Indicators/SellerSummary", `organizationId=${effectiveOrgId}&startDate=${start}&endDate=${end}`),
        API.getAction("Dashboard/SalesBySellerGeneral", `OrganizationId=${effectiveOrgId}`),
        API.getAction("Reports/AgentVisits", `organizationId=${effectiveOrgId}&startDate=${start}&endDate=${end}`),
      ])

      if (sellerRes.data.status === "ok") setSellers(sellerRes.data.response)
      if (salesRes.data.status === "ok") setSalesByMonth(salesRes.data.response)
      if (agentRes.data.status === "ok") setAgentVisits(agentRes.data.response)
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }, [effectiveOrgId])

  useEffect(() => {
    loadOrganizations()
  }, [loadOrganizations])

  useEffect(() => {
    if (!effectiveOrgId) {
      clearData()
      return
    }

    bindData()
  }, [effectiveOrgId, bindData, clearData])

  // Totals
  const totalSales = sellers.reduce((sum, s) => sum + s.totalSales, 0)
  const totalQuotes = sellers.reduce((sum, s) => sum + s.totalQuotes, 0)
  const avgConversion = sellers.length > 0 ? Math.round(sellers.reduce((sum, s) => sum + s.conversionRate, 0) / sellers.length * 10) / 10 : 0
  const totalUnpaid = sellers.reduce((sum, s) => sum + s.unpaidAmount, 0)

  // Build sales chart by seller
  const buildSalesChart = () => {
    if (!salesByMonth.length) return null

    // Get all unique months and sellers
    const months = [...new Set(salesByMonth.map((d: any) => `${d.year}-${String(d.month).padStart(2, "0")}`))]
      .sort()
    const sellerNames = [...new Set(salesByMonth.map((d: any) => d.vendedorNombre))]

    const datasets = sellerNames.map((name, i) => ({
      label: name,
      data: months.map(m => {
        const [y, mo] = m.split("-").map(Number)
        const match = salesByMonth.find((d: any) => d.vendedorNombre === name && d.year === y && d.month === mo)
        return match ? match.amount : 0
      }),
      borderColor: CHART_COLORS[i % CHART_COLORS.length],
      backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + "33",
      tension: 0.3,
      fill: false,
    }))

    return { labels: months, datasets }
  }

  const salesChart = buildSalesChart()

  return (
    <Container fluid className="px-4 py-3">
      <SuperAdminBackLink />

      <h4 className="mb-3">
        <i className="fas fa-user-tie me-2 text-primary" />
        {t("Indicators")} - {t("By Sellers")}
      </h4>

      {canSelectOrganization && (
        <Row className="g-2 align-items-end mb-3">
          <Col md={4} lg={3}>
            <Form.Label className="small text-muted mb-1">{t("Organization")}</Form.Label>
            <Form.Select
              size="sm"
              value={selectedOrganizationId}
              onChange={e => setSelectedOrganizationId(e.target.value)}
              disabled={isLoadingOrganizations}
            >
              {!selectedOrganizationId && <option value="">{t("Select Organization")}</option>}
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      )}

      <DateRangeFilter onFilter={(s, e) => bindData(s, e)} loading={isLoading} />

      {canSelectOrganization && !selectedOrganizationId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load indicators")}
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Total Sales")} value={totalSales} format="money" icon="fa-dollar-sign" color="#198754" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Total Quotes")} value={totalQuotes} format="money" icon="fa-file-invoice" color="#0d6efd" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Conversion Rate")} value={avgConversion} format="percent" icon="fa-chart-pie" color="#6f42c1" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Unpaid Invoices")} value={totalUnpaid} format="money" icon="fa-exclamation-triangle" color="#dc3545" />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={12}>
              <SellerRanking data={sellers} />
            </Col>
          </Row>

          {salesChart && (
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom">
                    <h6 className="mb-0">
                      <i className="fas fa-chart-line me-2 text-primary" />
                      {t("Sales by Month by Seller")}
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Line
                      data={salesChart}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: { position: "bottom" as const },
                        },
                        scales: {
                          y: { beginAtZero: true }
                        }
                      }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {agentVisits.length > 0 && (
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom">
                    <h6 className="mb-0">
                      <i className="fas fa-walking me-2 text-primary" />
                      {t("Agent Field Visits")}
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="ps-3">{t("Agent")}</th>
                            <th className="text-center">{t("Assigned")}</th>
                            <th className="text-center">{t("Visited")}</th>
                            <th className="text-center">{t("Not Visited")}</th>
                            <th className="text-center">{t("In Sale")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentVisits.map((agent: any, i: number) => (
                            <tr key={i}>
                              <td className="ps-3 fw-semibold">{agent.agentName || agent.name}</td>
                              <td className="text-center">{agent.totalAssigned ?? agent.assigned}</td>
                              <td className="text-center text-success">{agent.totalVisited ?? agent.visited}</td>
                              <td className="text-center text-danger">{agent.totalNotVisited ?? agent.notVisited}</td>
                              <td className="text-center text-primary">{agent.totalInSale ?? agent.inSale}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  )
}
